import { useMutation, useQueryClient, UseMutationOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { ActionPlanSummary } from "@mutiny-group/sbo-openapi-client";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { toast } from "react-toastify";
import { useAnalytics } from "@/hooks/useAnalytics";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

type ApproveScenarioForActionPlanOptions = {
  scenarioBuilderClient: ScenarioBuilderClient;
  actionPlanUuid: string;
  scenarioId: string;
};

type ApproveScenarioVariables = Omit<ApproveScenarioForActionPlanOptions, "scenarioBuilderClient">;

type UseApproveScenarioForActionPlanOptions = {
  config?: UseMutationOptions<ActionPlanSummary, AxiosError, ApproveScenarioVariables>;
};

const approveScenarioForActionPlan = ({
  scenarioBuilderClient,
  actionPlanUuid,
  scenarioId,
}: ApproveScenarioForActionPlanOptions) => {
  return scenarioBuilderClient.actionPlans.approveScenarioForActionPlan({
    actionPlanUuid,
    approveScenarioRequest: {
      scenarioId,
    },
  });
};

export const useApproveScenarioForActionPlan = ({
  config,
}: UseApproveScenarioForActionPlanOptions = {}) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const queryClient = useQueryClient();
  const { scenarioRecommendationApprovedForActionPlan, scenarioApprovedForActionPlan } =
    useAnalytics();
  const location = useLocation();
  const isFromRecommendations = !!(location.state as { actionPlanUuid?: string })?.actionPlanUuid;

  return useMutation({
    mutationFn: ({ actionPlanUuid, scenarioId }: ApproveScenarioVariables) =>
      approveScenarioForActionPlan({ scenarioBuilderClient, actionPlanUuid, scenarioId }),

    onSuccess: async (actionPlan: ActionPlanSummary) => {
      const { uuid: actionPlanUuid } = actionPlan;
      toast.success("Scenario approved successfully!");
      const eventProps = {
        actionPlanId: actionPlanUuid,
        actionPlanName: actionPlan.name,
        actionPlanLength: dayjs(actionPlan.endDate).diff(dayjs(actionPlan.startDate), "months"),
        actionPlanSpend: actionPlan.spendPlannedTotal,
        actionPlanStatus: actionPlan.status,
        actionPlanRevenueGoal: actionPlan.revenueTarget,
        actionPlanRevenueForecast: actionPlan.revenueForecastToDate,
      };
      if (isFromRecommendations) {
        scenarioRecommendationApprovedForActionPlan(eventProps);
      } else {
        scenarioApprovedForActionPlan(eventProps);
      }

      if (actionPlanUuid) {
        await queryClient.invalidateQueries({ queryKey: ["action-plan", actionPlanUuid] });
      }
      await queryClient.invalidateQueries({ queryKey: ["action-plans"] });
      await queryClient.invalidateQueries({ queryKey: ["scenario-v2"] });
      await queryClient.invalidateQueries({ queryKey: ["scenario-results-v2"] });
    },

    onError: (error) => {
      toast.error("Failed to approve scenario. Please try again.");
      console.error("useApproveScenarioForActionPlan onError", error);
    },
    ...config,
  });
};
